import { SyncBatch, SyncRevisionMap, SyncRevisions, SyncScope } from '../interface/sync.interface';

const NO_REVISIONS: SyncRevisions = { requested: 0, dispatched: 0, outstanding: [] };

export function revisionsOf(map: SyncRevisionMap, scope: SyncScope): SyncRevisions {
  return map[scope] ?? NO_REVISIONS;
}

export function withRequested(map: SyncRevisionMap, scope: SyncScope): SyncRevisionMap {
  const current = revisionsOf(map, scope);
  return { ...map, [scope]: { ...current, requested: current.requested + 1 } };
}

/**
 * The debounce fired: everything requested so far is dispatched at once, and the request carrying it, if one is
 * sent, is registered in the same step so nothing can observe the revisions as dispatched but not running.
 */
export function withDispatched(map: SyncRevisionMap, scope: SyncScope, sent: boolean): [SyncRevisionMap, SyncBatch] {
  const current = revisionsOf(map, scope);
  const batch: SyncBatch = { from: current.dispatched + 1, to: current.requested };
  const outstanding = sent ? [...current.outstanding, batch] : current.outstanding;
  return [{ ...map, [scope]: { ...current, dispatched: current.requested, outstanding } }, batch];
}

/** A request finished, whatever its outcome. */
export function withFinished(map: SyncRevisionMap, scope: SyncScope, batch: SyncBatch): SyncRevisionMap {
  const current = revisionsOf(map, scope);
  return { ...map, [scope]: { ...current, outstanding: current.outstanding.filter((entry) => entry !== batch) } };
}

export function requestedRevisions(map: SyncRevisionMap): Partial<Record<SyncScope, number>> {
  return Object.fromEntries(Object.entries(map).map(([scope, revisions]) => [scope, revisions.requested]));
}

/**
 * Every write requested up to each target has been sent and has finished. A later request that carries the
 * target's revision still has to finish, and a newer request finishing first settles nothing older.
 */
export function isSettledThrough(map: SyncRevisionMap, targets: Partial<Record<SyncScope, number>>): boolean {
  return (Object.entries(targets) as Array<[SyncScope, number]>).every(([scope, target]) => {
    const revisions = revisionsOf(map, scope);
    return revisions.dispatched >= target && revisions.outstanding.every((batch) => batch.from > target);
  });
}
